import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "emailField", "emailError",
    "passwordField", "passwordStrength", "strengthIndicator", "strengthBar",
    "passwordConfirmationField", "passwordConfirmationError",
    "termsCheckbox", "privacyCheckbox", "submitButton"
  ]

  connect() {
    this.validateForm()
  }

  validateEmail() {
    const email = this.emailFieldTarget.value
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    
    if (email.length === 0) {
      this.hideEmailError()
    } else if (email.length > 254) {
      this.showEmailError("メールアドレスが長すぎます")
    } else if (!emailRegex.test(email)) {
      this.showEmailError("有効なメールアドレスを入力してください")
    } else {
      this.hideEmailError()
    }
    
    this.validateForm()
  }

  validatePassword() {
    const password = this.passwordFieldTarget.value
    
    if (password.length === 0) {
      this.hidePasswordStrength()
    } else {
      this.showPasswordStrength(password)
    }
    
    this.validateForm()
  }

  validatePasswordConfirmation() {
    const password = this.passwordFieldTarget.value
    const confirmation = this.passwordConfirmationFieldTarget.value
    
    if (confirmation.length === 0) {
      this.hidePasswordConfirmationError()
    } else if (password !== confirmation) {
      this.showPasswordConfirmationError("パスワードが一致しません")
    } else {
      this.hidePasswordConfirmationError()
    }
    
    this.validateForm()
  }

  validateTerms() {
    this.validateForm()
  }

  validateForm() {
    const email = this.emailFieldTarget.value
    const password = this.passwordFieldTarget.value
    const confirmation = this.passwordConfirmationFieldTarget.value
    const termsChecked = this.termsCheckboxTarget.checked
    const privacyChecked = this.privacyCheckboxTarget.checked
    
    const emailValid = this.isEmailValid(email)
    const passwordValid = this.isPasswordValid(password)
    const confirmationValid = password === confirmation && confirmation.length > 0
    const termsValid = termsChecked && privacyChecked
    
    const formValid = emailValid && passwordValid && confirmationValid && termsValid
    
    this.submitButtonTarget.disabled = !formValid
  }

  isEmailValid(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return email.length > 0 && email.length <= 254 && emailRegex.test(email)
  }

  isPasswordValid(password) {
    // 8文字以上、英数字記号を組み合わせ
    const hasMinLength = password.length >= 8
    const hasLetter = /[a-zA-Z]/.test(password)
    const hasNumber = /\d/.test(password)
    const hasSymbol = /[\W_]/.test(password)
    
    return hasMinLength && hasLetter && hasNumber && hasSymbol
  }

  getPasswordStrength(password) {
    let score = 0
    
    // Length check
    if (password.length >= 8) score += 1
    if (password.length >= 12) score += 1
    
    // Character variety
    if (/[a-z]/.test(password)) score += 1
    if (/[A-Z]/.test(password)) score += 1
    if (/\d/.test(password)) score += 1
    if (/[\W_]/.test(password)) score += 1
    
    return score
  }

  showEmailError(message) {
    this.emailErrorTarget.textContent = message
    this.emailErrorTarget.classList.remove("hidden")
    this.emailFieldTarget.classList.add("border-red-500")
    this.emailFieldTarget.classList.remove("border-gray-300")
  }

  hideEmailError() {
    this.emailErrorTarget.classList.add("hidden")
    this.emailFieldTarget.classList.remove("border-red-500")
    this.emailFieldTarget.classList.add("border-gray-300")
  }

  showPasswordStrength(password) {
    const strength = this.getPasswordStrength(password)
    
    this.passwordStrengthTarget.classList.remove("hidden")
    
    let strengthText, strengthColor, barWidth
    
    if (strength <= 2) {
      strengthText = "弱い"
      strengthColor = "text-red-500"
      barWidth = "33%"
      this.strengthBarTarget.className = "h-1 rounded-full transition-all duration-300 bg-red-500"
    } else if (strength <= 4) {
      strengthText = "普通"
      strengthColor = "text-yellow-500"
      barWidth = "66%"
      this.strengthBarTarget.className = "h-1 rounded-full transition-all duration-300 bg-yellow-500"
    } else {
      strengthText = "強い"
      strengthColor = "text-green-500"
      barWidth = "100%"
      this.strengthBarTarget.className = "h-1 rounded-full transition-all duration-300 bg-green-500"
    }
    
    this.strengthIndicatorTarget.textContent = strengthText
    this.strengthIndicatorTarget.className = `text-xs font-medium ${strengthColor}`
    this.strengthBarTarget.style.width = barWidth
  }

  hidePasswordStrength() {
    this.passwordStrengthTarget.classList.add("hidden")
  }

  showPasswordConfirmationError(message) {
    this.passwordConfirmationErrorTarget.textContent = message
    this.passwordConfirmationErrorTarget.classList.remove("hidden")
    this.passwordConfirmationFieldTarget.classList.add("border-red-500")
    this.passwordConfirmationFieldTarget.classList.remove("border-gray-300")
  }

  hidePasswordConfirmationError() {
    this.passwordConfirmationErrorTarget.classList.add("hidden")
    this.passwordConfirmationFieldTarget.classList.remove("border-red-500")
    this.passwordConfirmationFieldTarget.classList.add("border-gray-300")
  }
}
